import { useAuth } from "@/contexts/AuthContext";
import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { useTenant } from "@/contexts/TenantContext";
import { useI18n } from "@/contexts/I18nContext";
import { track, EVENTS } from "@/lib/analytics";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, LabelList,
  PieChart, Pie, LineChart, Line, Legend,
} from "recharts";
import {
  Download, FileText, FileSpreadsheet, CheckCircle2, Sparkles, Calendar,
  ShieldAlert, Zap, History, Bot, Users, Lock,
} from "lucide-react";
import { toast } from "sonner";

const tooltipStyle = {
  contentStyle: { background: "#020617", border: "1px solid #1E293B", borderRadius: 2, fontFamily: "IBM Plex Mono", fontSize: 11 },
  labelStyle: { color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.1em", fontSize: 10 },
};

const CARRIER_COLORS = { Claro: "#EF4444", Vivo: "#A855F7", Oi: "#10B981" };
const ATTACK_COLORS = ["#EF4444", "#F59E0B", "#3B82F6", "#A855F7", "#10B981"];

function SuccessFlash({ doc, onDismiss, locale }) {
  return (
    <div className="absolute inset-0 z-10 bg-[#0F172A]/95 backdrop-blur-sm flex flex-col items-center justify-center text-center p-6" data-testid="export-success">
      <div className="relative">
        <div className="w-16 h-16 rounded-full bg-emerald-500/20 border-2 border-emerald-500 flex items-center justify-center">
          <CheckCircle2 className="w-8 h-8 text-emerald-400" />
        </div>
        <Sparkles className="absolute -top-1 -right-1 w-5 h-5 text-emerald-300 evos-blink" />
      </div>
      <h4 className="font-display text-2xl font-black mt-4 mb-1 text-emerald-300">
        {locale === "pt" ? "Relatório Gerado" : "Report Ready"}
      </h4>
      <p className="text-xs text-slate-400 mb-4 max-w-xs">{doc.message}</p>
      <div className="border border-slate-800 bg-[#020617] px-4 py-3 text-left mb-5 w-full max-w-sm">
        <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-slate-500 mb-1">{locale === "pt" ? "Documento" : "Document"}</div>
        <div className="font-mono text-xs text-slate-200 truncate">{doc.filename}</div>
        <div className="text-[10px] font-mono text-slate-500 mt-1">{doc.format} · {doc.size_kb} KB · {doc.doc_id}</div>
      </div>
      <button data-testid="export-dismiss" onClick={onDismiss} className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold uppercase tracking-wider text-xs px-4 py-2 rounded-sm">
        {locale === "pt" ? "Concluído" : "Done"}
      </button>
    </div>
  );
}

function PeriodFilter({ period, setPeriod, customFrom, setCustomFrom, customTo, setCustomTo, locale }) {
  const labels = {
    today: locale === "pt" ? "Hoje" : "Today",
    "7d":   locale === "pt" ? "Últimos 7 dias" : "Last 7 days",
    "30d":  locale === "pt" ? "Últimos 30 dias" : "Last 30 days",
    custom: locale === "pt" ? "Personalizado" : "Custom",
  };
  return (
    <div className="border border-slate-800 bg-[#0F172A] p-4 flex flex-wrap items-center gap-3" data-testid="period-filter">
      <div className="flex items-center gap-2 mr-2">
        <Calendar className="w-4 h-4 text-[#60A5FA]" />
        <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-slate-400">
          {locale === "pt" ? "Período" : "Period"}
        </span>
      </div>
      {Object.keys(labels).map((k) => (
        <button
          key={k}
          data-testid={`period-${k}`}
          onClick={() => setPeriod(k)}
          className={`text-xs font-mono uppercase tracking-wider px-3 py-1.5 border ${
            period === k ? "border-[#3B82F6] bg-[#1E3A8A]/25 text-white" : "border-slate-800 text-slate-400 hover:border-slate-700"
          }`}
        >
          {labels[k]}
        </button>
      ))}
      {period === "custom" && (
        <div className="flex items-center gap-2 ml-2">
          <input type="date" data-testid="period-from" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="bg-[#020617] border border-slate-800 px-2 py-1 text-xs font-mono text-slate-200" />
          <span className="text-slate-500 text-xs">→</span>
          <input type="date" data-testid="period-to" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="bg-[#020617] border border-slate-800 px-2 py-1 text-xs font-mono text-slate-200" />
        </div>
      )}
    </div>
  );
}

export default function Analytics() {
  const { tenant } = useTenant();
  const { user } = useAuth();
  const { t, locale } = useI18n();
  const isAdmin = !!user?.is_admin;
  const [period, setPeriod] = useState("7d");
  const [customFrom, setCustomFrom] = useState(() => new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10));
  const [customTo, setCustomTo] = useState(() => new Date().toISOString().slice(0, 10));

  const [sla, setSla] = useState([]);
  const [breakdown, setBreakdown] = useState([]);
  const [socReport, setSocReport] = useState(null);
  const [sdwanReport, setSdwanReport] = useState(null);
  const [accessLogs, setAccessLogs] = useState([]);
  const [leads, setLeads] = useState([]);
  const [demos, setDemos] = useState([]);
  const [exportState, setExportState] = useState(null);

  const params = useMemo(() => {
    const p = new URLSearchParams({ tenant: tenant?.id || "sedu-pe", period });
    if (period === "custom") { p.set("custom_from", customFrom); p.set("custom_to", customTo); }
    return p.toString();
  }, [tenant, period, customFrom, customTo]);

  useEffect(() => {
    if (!tenant) return;
    api.get(`/analytics/sla-per-unit?tenant=${tenant.id}`).then(({ data }) => setSla(data));
    api.get(`/analytics/security-breakdown?tenant=${tenant.id}`).then(({ data }) => setBreakdown(data));
    if (isAdmin) {
      api.get(`/access/logs`).then(({ data }) => setAccessLogs(data)).catch(() => setAccessLogs([]));
      api.get(`/landing/leads`).then(({ data }) => setLeads(data)).catch(() => setLeads([]));
      api.get(`/landing/commercial-leads`).then(({ data }) => setDemos(data)).catch(() => setDemos([]));
    } else {
      setAccessLogs([]);
      setLeads([]);
      setDemos([]);
    }
  }, [tenant, isAdmin]);

  useEffect(() => {
    if (!tenant) return;
    api.get(`/reports/soc?${params}`).then(({ data }) => setSocReport(data));
    api.get(`/reports/sdwan?${params}`).then(({ data }) => setSdwanReport(data));
  }, [tenant, params]);

  const handleExport = async (fmt) => {
    setExportState({ fmt, busy: true, doc: null });
    track(EVENTS.EXPORT_REPORT, { format: fmt, period });
    try {
      const { data } = await api.post(`/analytics/export`, { fmt, report: "executive-summary", tenant: tenant.id });
      setExportState({ fmt, busy: false, doc: data });
      toast.success(`${fmt.toUpperCase()} ${locale === "pt" ? "pronto" : "ready"}`, { description: data.filename });
    } catch {
      toast.error(locale === "pt" ? "Falha na exportação" : "Export failed");
      setExportState(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div className="border-b border-slate-800 pb-5">
        <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 font-mono mb-2">
          {locale === "pt" ? "Relatórios & SLA · Analytics avançado" : "Analytics & Reports · Advanced"}
        </div>
        <h1 className="font-display text-4xl font-black tracking-tighter">
          {locale === "pt" ? "Relatórios & SLA" : "Operations Analytics"}
        </h1>
        <p className="text-sm text-slate-400 mt-1">
          {locale === "pt"
            ? "SLA por unidade, conformidade, relatórios de SOC e SD-WAN com filtros dinâmicos de período."
            : "SLA per unit, compliance, SOC and SD-WAN reports with dynamic period filters."}
        </p>
      </div>

      {/* Period filter */}
      <PeriodFilter
        period={period}
        setPeriod={setPeriod}
        customFrom={customFrom}
        setCustomFrom={setCustomFrom}
        customTo={customTo}
        setCustomTo={setCustomTo}
        locale={locale}
      />

      {/* SOC report */}
      <div className="border border-slate-800 bg-[#0F172A]" data-testid="soc-report">
        <div className="border-b border-slate-800 px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldAlert className="w-4 h-4 text-red-400" />
            <h3 className="text-xs uppercase tracking-[0.25em] font-mono font-semibold">
              {locale === "pt" ? "Relatório SOC · Incidentes de Segurança" : "SOC Report · Security Incidents"}
            </h3>
          </div>
          <span className="text-[10px] font-mono text-slate-500">{socReport?.days || 0} {locale === "pt" ? "dias" : "days"}</span>
        </div>
        {socReport && (
          <div className="p-5 space-y-5">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label={locale === "pt" ? "Bloqueados" : "Blocked"} value={socReport.summary.blocked_total.toLocaleString()} color="#EF4444" />
              <Stat label={locale === "pt" ? "Mitigados" : "Mitigated"} value={socReport.summary.mitigated_total.toLocaleString()} color="#10B981" />
              <Stat label="MTTR" value={`${socReport.summary.mttr_minutes} min`} color="#F59E0B" />
              <Stat label={locale === "pt" ? "Ativos" : "Active"} value={socReport.summary.active_now} color="#A855F7" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
              <div className="lg:col-span-7 border border-slate-800 bg-[#020617] p-4">
                <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-slate-500 mb-3">
                  {locale === "pt" ? "Incidentes bloqueados · diário" : "Incidents blocked · daily"}
                </div>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={socReport.daily}>
                    <CartesianGrid stroke="#1E293B" strokeDasharray="3 3" />
                    <XAxis dataKey="date" stroke="#475569" fontSize={9} />
                    <YAxis stroke="#475569" fontSize={9} />
                    <Tooltip {...tooltipStyle} />
                    <Bar dataKey="blocked" fill="#EF4444" radius={[2, 2, 0, 0]} />
                    <Bar dataKey="mitigated" fill="#10B981" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="lg:col-span-5 border border-slate-800 bg-[#020617] p-4">
                <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-slate-500 mb-3">
                  {locale === "pt" ? "Tipos de ataque mais comuns" : "Most common attack types"}
                </div>
                <div className="space-y-2.5">
                  {socReport.attack_types.map((a, i) => (
                    <div key={a.type} data-testid={`attack-${i}`}>
                      <div className="flex justify-between text-[11px] font-mono mb-1">
                        <span className="text-slate-300">{a.type}</span>
                        <span className="text-slate-400">{a.count.toLocaleString()}</span>
                      </div>
                      <div className="h-1.5 bg-slate-800">
                        <div className="h-full" style={{ width: `${Math.min(100, (a.count / Math.max(...socReport.attack_types.map((x) => x.count))) * 100)}%`, background: ATTACK_COLORS[i % ATTACK_COLORS.length] }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* SD-WAN report */}
      <div className="border border-slate-800 bg-[#0F172A]" data-testid="sdwan-report">
        <div className="border-b border-slate-800 px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-400" />
            <h3 className="text-xs uppercase tracking-[0.25em] font-mono font-semibold">
              {locale === "pt" ? "Relatório SD-WAN · Disponibilidade & Failover" : "SD-WAN Report · Uptime & Failover"}
            </h3>
          </div>
          <span className="text-[10px] font-mono text-slate-500">
            {sdwanReport?.summary.total_failovers || 0} {locale === "pt" ? "eventos" : "events"}
          </span>
        </div>
        {sdwanReport && (
          <div className="p-5 space-y-5">
            <div className="grid grid-cols-3 gap-3">
              {sdwanReport.carriers.map((c) => (
                <div key={c} className="border border-slate-800 bg-[#020617] p-3" data-testid={`carrier-uptime-${c.toLowerCase()}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-mono font-bold text-sm" style={{ color: CARRIER_COLORS[c] }}>{c}</span>
                    <span className="text-[9px] font-mono text-slate-500 uppercase">{locale === "pt" ? "Disponib." : "Uptime"}</span>
                  </div>
                  <div className="text-2xl font-mono font-bold text-slate-100">{sdwanReport.summary.avg_uptime[c]}%</div>
                </div>
              ))}
            </div>

            <div className="border border-slate-800 bg-[#020617] p-4">
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-slate-500 mb-3">
                {locale === "pt" ? "Uptime médio diário · Claro · Vivo · Oi" : "Avg daily uptime · Claro · Vivo · Oi"}
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <LineChart data={sdwanReport.daily_uptime}>
                  <CartesianGrid stroke="#1E293B" strokeDasharray="3 3" />
                  <XAxis dataKey="date" stroke="#475569" fontSize={9} />
                  <YAxis stroke="#475569" fontSize={9} domain={[95, 100]} />
                  <Tooltip {...tooltipStyle} />
                  <Legend wrapperStyle={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} iconSize={8} />
                  {sdwanReport.carriers.map((c) => (
                    <Line key={c} type="monotone" dataKey={c} stroke={CARRIER_COLORS[c]} strokeWidth={2} dot={false} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>

            <div className="border border-slate-800 bg-[#020617]" data-testid="failover-history">
              <div className="border-b border-slate-800 px-4 py-2 text-[10px] font-mono uppercase tracking-[0.25em] text-slate-500">
                {locale === "pt" ? "Histórico de eventos de failover" : "Failover events history"}
              </div>
              <div className="max-h-[260px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-[#020617]">
                    <tr className="text-[10px] uppercase font-mono text-slate-500 border-b border-slate-800">
                      <th className="text-left px-4 py-2">{locale === "pt" ? "Quando" : "When"}</th>
                      <th className="text-left px-4 py-2">{locale === "pt" ? "Unidade" : "Unit"}</th>
                      <th className="text-left px-4 py-2">{locale === "pt" ? "Primário" : "Primary"}</th>
                      <th className="text-left px-4 py-2">{locale === "pt" ? "Backup" : "Backup"}</th>
                      <th className="text-left px-4 py-2">{locale === "pt" ? "Disparo" : "Trigger"}</th>
                      <th className="text-right px-4 py-2">{locale === "pt" ? "Duração" : "Duration"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sdwanReport.failover_events.map((f) => (
                      <tr key={f.id} className="border-b border-slate-800/40">
                        <td className="px-4 py-2 font-mono text-[11px] text-slate-300">{new Date(f.ts).toLocaleString(locale === "pt" ? "pt-BR" : "en-GB", { hour12: false })}</td>
                        <td className="px-4 py-2 text-xs text-slate-200">{f.unit}</td>
                        <td className="px-4 py-2 text-xs font-mono" style={{ color: CARRIER_COLORS[f.primary] }}>{f.primary}</td>
                        <td className="px-4 py-2 text-xs font-mono" style={{ color: CARRIER_COLORS[f.backup] }}>→ {f.backup}</td>
                        <td className="px-4 py-2 text-[11px] text-slate-400">{f.trigger}</td>
                        <td className="px-4 py-2 text-right font-mono text-xs text-slate-300">{f.duration_min} min</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* SLA per Unit + Security Pie + Export */}
      <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
        <div className="xl:col-span-7 border border-slate-800 bg-[#0F172A]">
          <div className="border-b border-slate-800 px-5 py-3">
            <h3 className="text-xs uppercase tracking-[0.25em] font-mono font-semibold">
              {locale === "pt" ? "SLA · Conformidade por Unidade Remota" : "SLA Compliance · Per Remote Unit"}
            </h3>
            <div className="text-[10px] text-slate-500 mt-0.5">{locale === "pt" ? "30 dias · realizado vs meta 99,5%" : "30-day actual vs 99.5% target"}</div>
          </div>
          <div className="p-5" data-testid="sla-chart">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={sla} margin={{ top: 8, right: 8, left: -10, bottom: 60 }}>
                <CartesianGrid stroke="#1E293B" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="unit_name" stroke="#94A3B8" fontSize={10} interval={0} angle={-30} textAnchor="end" height={70} />
                <YAxis stroke="#475569" fontSize={10} domain={[98, 100]} />
                <Tooltip {...tooltipStyle} formatter={(v) => `${v}%`} />
                <Bar dataKey="actual" radius={[2, 2, 0, 0]}>
                  {sla.map((s, i) => <Cell key={i} fill={s.actual >= s.target ? "#10B981" : "#F59E0B"} />)}
                  <LabelList dataKey="actual" position="top" fill="#94A3B8" fontSize={9} fontFamily="IBM Plex Mono" formatter={(v) => `${v}%`} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="xl:col-span-5 border border-slate-800 bg-[#0F172A] relative overflow-hidden">
          <div className="border-b border-slate-800 px-5 py-3 flex items-center gap-2">
            <Download className="w-4 h-4 text-[#60A5FA]" />
            <h3 className="text-xs uppercase tracking-[0.25em] font-mono font-semibold">
              {locale === "pt" ? "Exportar Relatório" : "Export Report"}
            </h3>
          </div>
          <div className="p-5 space-y-5">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-slate-500 mb-1">{locale === "pt" ? "Relatório selecionado" : "Selected Report"}</div>
              <div className="font-display font-black text-xl">{locale === "pt" ? "Sumário Executivo" : "Executive Summary"}</div>
              <div className="text-xs text-slate-400 mt-1">
                {locale === "pt" ? "Inclui SLA por unidade, postura de segurança, top 10 incidentes e recomendações do Copilot." : "Includes SLA per unit, security posture, top 10 incidents and Copilot recommendations."}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <ExportButton icon={FileText} label={locale === "pt" ? "Exportar PDF" : "Export PDF"} color="#EF4444" busy={exportState?.fmt === "pdf" && exportState?.busy} onClick={() => handleExport("pdf")} testid="export-pdf-btn" />
              <ExportButton icon={FileSpreadsheet} label={locale === "pt" ? "Exportar CSV" : "Export CSV"} color="#10B981" busy={exportState?.fmt === "csv" && exportState?.busy} onClick={() => handleExport("csv")} testid="export-csv-btn" />
            </div>
            <div className="border border-dashed border-slate-800 p-4 bg-[#020617]">
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-slate-500 mb-2">{locale === "pt" ? "Exportações recentes" : "Recent Exports"}</div>
              <div className="text-[11px] font-mono text-slate-400 space-y-1">
                <div className="flex justify-between"><span>EVOS-exec-summary-2026.pdf</span><span className="text-slate-500">1.4 MB</span></div>
                <div className="flex justify-between"><span>EVOS-sla-monthly.csv</span><span className="text-slate-500">82 KB</span></div>
              </div>
            </div>
          </div>
          {exportState?.doc && <SuccessFlash doc={exportState.doc} onDismiss={() => setExportState(null)} locale={locale} />}
        </div>
      </div>

      {/* Security Compliance breakdown */}
      <div className="border border-slate-800 bg-[#0F172A]">
        <div className="border-b border-slate-800 px-5 py-3">
          <h3 className="text-xs uppercase tracking-[0.25em] font-mono font-semibold">
            {locale === "pt" ? "Conformidade · Famílias de Controle" : "Security Compliance · Breakdown"}
          </h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-5" data-testid="security-pie-section">
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={breakdown} dataKey="compliant" nameKey="control" innerRadius={55} outerRadius={100} stroke="#020617" strokeWidth={2} paddingAngle={2}>
                {breakdown.map((d, i) => <Cell key={i} fill={d.color} />)}
              </Pie>
              <Tooltip {...tooltipStyle} formatter={(v) => `${v}%`} />
            </PieChart>
          </ResponsiveContainer>
          <div className="space-y-2">
            {breakdown.map((c) => (
              <div key={c.control} className="flex items-center gap-3 py-2 border-b border-slate-800/60">
                <span className="w-2.5 h-2.5 rounded-sm" style={{ background: c.color }} />
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-slate-200 font-semibold truncate">{c.control}</div>
                  <div className="h-1 bg-slate-800 mt-1.5 overflow-hidden"><div className="h-full" style={{ width: `${c.compliant}%`, background: c.color }} /></div>
                </div>
                <span className="font-mono text-sm font-bold text-slate-100">{c.compliant}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Captured leads (admin-only) — populated by the public landing form */}
      {isAdmin && (
        <div className="border border-slate-800 bg-[#0F172A]" data-testid="leads-table">
          <div className="border-b border-slate-800 px-5 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-[#10B981]" />
              <h3 className="text-xs uppercase tracking-[0.25em] font-mono font-semibold">
                {locale === "pt" ? "Leads de Acesso · Cockpit" : "Cockpit Access Leads"}
              </h3>
              <span className="text-[10px] font-mono font-bold px-2 py-0.5 border border-emerald-500/40 text-emerald-300 bg-emerald-500/10 ml-1">ADMIN</span>
            </div>
            <span className="text-[10px] font-mono text-slate-500">{leads.length} {locale === "pt" ? "leads" : "leads"}</span>
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-[#0F172A]">
                <tr className="text-[10px] uppercase font-mono text-slate-500 border-b border-slate-800">
                  <th className="text-left px-5 py-3">{locale === "pt" ? "Quando" : "When"}</th>
                  <th className="text-left px-5 py-3">{locale === "pt" ? "Nome" : "Name"}</th>
                  <th className="text-left px-5 py-3">E-mail</th>
                  <th className="text-left px-5 py-3">{locale === "pt" ? "Organização" : "Organization"}</th>
                  <th className="text-left px-5 py-3">{locale === "pt" ? "Cargo" : "Role"}</th>
                  <th className="text-left px-5 py-3">IP</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((row) => (
                  <tr key={row.log_id} className="border-b border-slate-800/40 hover:bg-slate-800/30" data-testid={`lead-row-${row.log_id}`}>
                    <td className="px-5 py-2.5 font-mono text-[11px] text-slate-300">{new Date(row.ts).toLocaleString(locale === "pt" ? "pt-BR" : "en-GB", { hour12: false })}</td>
                    <td className="px-5 py-2.5 text-xs text-slate-100 font-semibold">{row.name}</td>
                    <td className="px-5 py-2.5 font-mono text-xs text-slate-300">{row.email}</td>
                    <td className="px-5 py-2.5 text-xs text-slate-200">{row.organizacao || "—"}</td>
                    <td className="px-5 py-2.5 text-xs text-slate-400">{row.cargo || "—"}</td>
                    <td className="px-5 py-2.5 font-mono text-[11px] text-slate-500">{row.ip || "—"}</td>
                  </tr>
                ))}
                {!leads.length && <tr><td colSpan={6} className="px-5 py-10 text-center text-xs text-slate-500 font-mono">{locale === "pt" ? "Ainda não houve leads capturados" : "No leads captured yet"}</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Commercial demo requests (admin-only) */}
      {isAdmin && (
        <div className="border border-slate-800 bg-[#0F172A]" data-testid="demos-table">
          <div className="border-b border-slate-800 px-5 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-[#3B82F6]" />
              <h3 className="text-xs uppercase tracking-[0.25em] font-mono font-semibold">
                {locale === "pt" ? "Demos Executivas Solicitadas" : "Executive Demo Requests"}
              </h3>
              <span className="text-[10px] font-mono font-bold px-2 py-0.5 border border-[#3B82F6]/40 text-[#60A5FA] bg-[#3B82F6]/10 ml-1">ADMIN</span>
            </div>
            <span className="text-[10px] font-mono text-slate-500">{demos.length} {locale === "pt" ? "solicitações" : "requests"}</span>
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-[#0F172A]">
                <tr className="text-[10px] uppercase font-mono text-slate-500 border-b border-slate-800">
                  <th className="text-left px-5 py-3">{locale === "pt" ? "Quando" : "When"}</th>
                  <th className="text-left px-5 py-3">{locale === "pt" ? "Nome" : "Name"}</th>
                  <th className="text-left px-5 py-3">E-mail</th>
                  <th className="text-left px-5 py-3">{locale === "pt" ? "Telefone" : "Phone"}</th>
                  <th className="text-left px-5 py-3">{locale === "pt" ? "Organização" : "Organization"}</th>
                  <th className="text-left px-5 py-3">{locale === "pt" ? "Mensagem" : "Message"}</th>
                </tr>
              </thead>
              <tbody>
                {demos.map((row) => (
                  <tr key={row.lead_id} className="border-b border-slate-800/40 hover:bg-slate-800/30" data-testid={`demo-row-${row.lead_id}`}>
                    <td className="px-5 py-2.5 font-mono text-[11px] text-slate-300 whitespace-nowrap">{new Date(row.ts).toLocaleString(locale === "pt" ? "pt-BR" : "en-GB", { hour12: false })}</td>
                    <td className="px-5 py-2.5 text-xs text-slate-100 font-semibold">{row.nome}</td>
                    <td className="px-5 py-2.5 font-mono text-xs text-slate-300">{row.email}</td>
                    <td className="px-5 py-2.5 font-mono text-xs text-slate-300">{row.telefone}</td>
                    <td className="px-5 py-2.5 text-xs text-slate-200">{row.organizacao}</td>
                    <td className="px-5 py-2.5 text-[11px] text-slate-400 max-w-md">{row.mensagem || "—"}</td>
                  </tr>
                ))}
                {!demos.length && <tr><td colSpan={6} className="px-5 py-10 text-center text-xs text-slate-500 font-mono">{locale === "pt" ? "Nenhuma demo solicitada ainda" : "No demos requested yet"}</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Access audit (admin-only) */}
      {isAdmin ? (
        <div className="border border-slate-800 bg-[#0F172A]" data-testid="access-audit">
          <div className="border-b border-slate-800 px-5 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-[#60A5FA]" />
              <h3 className="text-xs uppercase tracking-[0.25em] font-mono font-semibold">
                {locale === "pt" ? "Auditoria de Acessos ao MVP" : "MVP Access Audit"}
              </h3>
              <span className="text-[10px] font-mono font-bold px-2 py-0.5 border border-emerald-500/40 text-emerald-300 bg-emerald-500/10 ml-1">
                ADMIN
              </span>
            </div>
            <span className="text-[10px] font-mono text-slate-500">{accessLogs.length} {locale === "pt" ? "registros" : "entries"}</span>
          </div>
          <div className="max-h-[360px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-[#0F172A]">
                <tr className="text-[10px] uppercase font-mono text-slate-500 border-b border-slate-800">
                  <th className="text-left px-5 py-3">{locale === "pt" ? "Quando" : "When"}</th>
                  <th className="text-left px-5 py-3">{locale === "pt" ? "Nome" : "Name"}</th>
                  <th className="text-left px-5 py-3">E-mail</th>
                  <th className="text-center px-5 py-3">{locale === "pt" ? "Origem" : "Source"}</th>
                </tr>
              </thead>
              <tbody>
                {accessLogs.map((row) => {
                  const isOAuth = row.source === "oauth_google";
                  return (
                    <tr key={row.log_id} className="border-b border-slate-800/40" data-testid={`access-row-${row.log_id}`}>
                      <td className="px-5 py-2.5 font-mono text-[11px] text-slate-300">{new Date(row.ts).toLocaleString(locale === "pt" ? "pt-BR" : "en-GB", { hour12: false })}</td>
                      <td className="px-5 py-2.5 text-xs text-slate-100 font-semibold">{row.name}</td>
                      <td className="px-5 py-2.5 font-mono text-xs text-slate-300">{row.email}</td>
                      <td className="px-5 py-2.5 text-center">
                        <span className={`text-[10px] font-mono font-bold px-2 py-0.5 border whitespace-nowrap ${isOAuth ? "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" : "text-amber-300 border-amber-500/40 bg-amber-500/10"}`}>
                          {isOAuth ? "OAUTH" : "LEAD"}
                        </span>
                      </td>
                    </tr>
                  );
                })}
                {!accessLogs.length && <tr><td colSpan={4} className="px-5 py-10 text-center text-xs text-slate-500 font-mono">{locale === "pt" ? "Ainda sem registros" : "No entries yet"}</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="border border-slate-800 bg-[#0F172A]" data-testid="access-audit-restricted">
          <div className="px-6 py-10 flex items-start gap-4">
            <div className="w-12 h-12 flex items-center justify-center border border-amber-500/40 bg-amber-500/10 flex-shrink-0">
              <Lock className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-amber-300 mb-1">
                {locale === "pt" ? "Acesso Restrito" : "Restricted Access"}
              </div>
              <h3 className="font-display font-bold text-xl tracking-tight mb-2">
                {locale === "pt" ? "Auditoria de Acessos · Privada" : "Access Audit · Private"}
              </h3>
              <p className="text-sm text-slate-400 max-w-xl leading-relaxed">
                {locale === "pt"
                  ? "Apenas administradores do sistema têm permissão para visualizar os logs de auditoria de acessos."
                  : "Only system administrators are authorized to view the access audit logs."}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="border border-slate-800 bg-[#020617] p-3">
      <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-slate-500">{label}</div>
      <div className="text-2xl font-mono font-bold mt-1" style={{ color }}>{value}</div>
    </div>
  );
}

function ExportButton({ icon: Icon, label, color, busy, onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick} disabled={busy}
      className="border bg-[#020617] hover:bg-[#0F172A] px-4 py-5 rounded-sm flex flex-col items-center gap-3 transition-colors disabled:opacity-60"
      style={{ borderColor: color + "55" }}>
      <Icon className="w-6 h-6" style={{ color }} />
      <span className="text-xs font-semibold uppercase tracking-wider" style={{ color }}>{busy ? "..." : label}</span>
    </button>
  );
}
